/**
 * ---------------------------------------------------------------------------------------------------------------------
 * AF — Website Development Briefing #1
 * ---------------------------------------------------------------------------------------------------------------------
 *
 * Two custom elements, kept in their own asset so a theme update never overwrites them:
 *
 *   <size-guard>                     Briefing #1.2 — require an explicit size choice before adding to cart.
 *   <line-item-quantity-selector>    Briefing #1.5 — minus/plus stepper for cart line items.
 *
 * Both are plain custom elements with no imports, so this file is loaded as a deferred classic script.
 */
(() => {
  'use strict';

  /* -------------------------------------------------------------------------------------------------------------------
   * <size-guard>
   *
   * Impact always preselects a variant. A customer who never touches the size selector therefore silently adds the
   * smallest available size to their cart, which is the bounce-rate problem described in the briefing.
   *
   * While no size has been chosen ("pending"):
   *   - the size swatches render without a selection ring and the option label shows "Select size";
   *   - hovering the add to cart button swaps its label to "Select size";
   *   - clicking add to cart (or the dynamic checkout button) is blocked and a red message appears under the sizes.
   *
   * The gate is skipped when the URL already carries a ?variant= parameter, because then the customer arrived on a
   * deliberately chosen variant (a size specific ad, a back in stock email, a shared link).
   * ---------------------------------------------------------------------------------------------------------------- */
  class SizeGuard extends HTMLElement {
    connectedCallback() {
      this._abortController = new AbortController();

      const { signal } = this._abortController;
      const formId = this.getAttribute('form');

      this.optionName = this.getAttribute('option');
      this.picker = formId ? document.querySelector(`variant-picker[form="${formId}"]`) : null;

      // Without a picker there is nothing to choose, so there is nothing to guard either.
      if (!this.picker || !this.optionName) {
        return;
      }

      this.pending = !new URLSearchParams(window.location.search).has('variant');
      this._render();

      // Only a real customer interaction clears the gate. The theme itself dispatches synthetic "change" events on the
      // option inputs whenever it re-syncs the selectors (for instance when picking a colour forces a different size),
      // and those must not count as a choice.
      this.picker.addEventListener('click', this._onOptionClicked.bind(this), { signal });
      this.picker.addEventListener('change', this._onOptionChanged.bind(this), { signal });

      // Delegated, because the buttons are not parsed yet when connectedCallback runs.
      this.addEventListener('click', this._onClick.bind(this), { capture: true, signal });
      this.addEventListener('mouseover', this._onPointerOver.bind(this), { signal });
      this.addEventListener('mouseout', this._onPointerOut.bind(this), { signal });
      this.addEventListener('focusin', this._onPointerOver.bind(this), { signal });
      this.addEventListener('focusout', this._onPointerOut.bind(this), { signal });
    }

    disconnectedCallback() {
      this._abortController?.abort();
    }

    get submitButton() {
      return this.querySelector('buy-buttons button[type="submit"]');
    }

    get sizeFieldset() {
      return this.picker?.querySelector('[data-size-option]');
    }

    _onOptionClicked(event) {
      if (!this.pending) {
        return;
      }

      const optionValue = event.target.closest?.('[data-option-value]');

      if (optionValue && this.sizeFieldset?.contains(optionValue)) {
        this._clear();
      }
    }

    _onOptionChanged(event) {
      // isTrusted rules out the theme's own programmatic re-syncing; it stays true for keyboard selection.
      if (this.pending && event.isTrusted && event.target.name === this.optionName) {
        this._clear();
      }
    }

    _onClick(event) {
      if (!this.pending) {
        return;
      }

      const trigger = event.target.closest?.('button[type="submit"], .shopify-payment-button');

      if (!trigger || !this.contains(trigger)) {
        return;
      }

      event.preventDefault();
      event.stopPropagation();

      const error = this.picker?.querySelector('[data-size-error]');

      if (error) {
        error.hidden = false;
      }

      this.sizeFieldset?.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
    }

    _onPointerOver(event) {
      const button = event.target.closest?.('button[type="submit"]');

      if (!this.pending || !button || !this.contains(button) || button.contains(event.relatedTarget)) {
        return;
      }

      const label = this._labelElement(button);
      const hoverLabel = this.getAttribute('hover-label');

      if (!label || !hoverLabel || this._originalLabel !== undefined) {
        return;
      }

      this._originalLabel = label.innerHTML;
      label.textContent = hoverLabel;
    }

    _onPointerOut(event) {
      const button = event.target.closest?.('button[type="submit"]');

      if (!button || !this.contains(button) || button.contains(event.relatedTarget)) {
        return;
      }

      this._restoreLabel(button);
    }

    /**
     * The theme wraps the button content in a div when the custom-button behaviour is applied, and writes the add to
     * cart / sold out label into that div. Fall back to the button itself when the wrapper is absent.
     */
    _labelElement(button) {
      return button?.querySelector(':scope > div') || button;
    }

    _restoreLabel(button) {
      const label = this._labelElement(button);

      if (label && this._originalLabel !== undefined) {
        label.innerHTML = this._originalLabel;
      }

      this._originalLabel = undefined;
    }

    _clear() {
      this.pending = false;
      this._restoreLabel(this.submitButton);
      this._render();
    }

    _render() {
      this.toggleAttribute('data-size-pending', this.pending);
      this.picker?.toggleAttribute('data-size-pending', this.pending);

      if (!this.pending) {
        const error = this.picker?.querySelector('[data-size-error]');

        if (error) {
          error.hidden = true;
        }
      }
    }
  }

  /* -------------------------------------------------------------------------------------------------------------------
   * <line-item-quantity-selector>
   *
   * Wraps the existing cart quantity input with minus/plus buttons. The theme's own <line-item-quantity> element
   * already listens for a bubbling "change" event coming from an input carrying data-line-key, so all this element has
   * to do is write the new value and dispatch that event — the cart update itself is unchanged.
   * ---------------------------------------------------------------------------------------------------------------- */
  class LineItemQuantitySelector extends HTMLElement {
    connectedCallback() {
      this._abortController = new AbortController();

      const { signal } = this._abortController;

      this.addEventListener('click', this._onClick.bind(this), { signal });
      this.addEventListener('change', () => this._syncButtons(), { signal });

      this._syncButtons();
    }

    disconnectedCallback() {
      this._abortController?.abort();
    }

    get input() {
      return this.querySelector('input[data-line-key]');
    }

    get quantity() {
      return parseInt(this.input?.value, 10) || 1;
    }

    get maxQuantity() {
      const max = parseInt(this.input?.getAttribute('max'), 10);

      return Number.isInteger(max) ? max : null;
    }

    _onClick(event) {
      const button = event.target.closest('[data-quantity-step]');

      if (!button || !this.contains(button) || button.disabled || !this.input) {
        return;
      }

      event.preventDefault();

      const step = parseInt(button.getAttribute('data-quantity-step'), 10);
      const max = this.maxQuantity;

      let target = this.quantity + step;

      target = Math.max(1, max === null ? target : Math.min(target, max));

      if (target === this.quantity) {
        return;
      }

      this.input.value = String(target);
      this._syncButtons();

      // Bubbles up to <line-item-quantity>, which performs the actual cart update.
      this.input.dispatchEvent(new Event('change', { bubbles: true }));
    }

    /**
     * Minus is disabled at 1 (removing is what the "Remove" link is for) and plus is disabled once the available
     * inventory is reached.
     */
    _syncButtons() {
      if (!this.input) {
        return;
      }

      const max = this.maxQuantity;
      const decrease = this.querySelector('[data-quantity-step="-1"]');
      const increase = this.querySelector('[data-quantity-step="1"]');

      if (decrease) {
        decrease.disabled = this.quantity <= 1;
      }

      if (increase) {
        increase.disabled = max !== null && this.quantity >= max;
      }
    }
  }

  if (!window.customElements.get('size-guard')) {
    window.customElements.define('size-guard', SizeGuard);
  }

  if (!window.customElements.get('line-item-quantity-selector')) {
    window.customElements.define('line-item-quantity-selector', LineItemQuantitySelector);
  }
})();
